import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useGetRechargeReceptionAddressesQuery } from '../../store/api/balanceApi';
import { ButtonSpinner } from '../ui/Loader';

interface BinanceClaimFormProps {
  onSubmit: (transactionId: string) => Promise<void>;
  isLoading?: boolean;
}

export function BinanceClaimForm({ onSubmit, isLoading = false }: BinanceClaimFormProps) {
  const { t } = useTranslation();
  const [transactionId, setTransactionId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: receptionAddressesResponse, isFetching: isFetchingReceptionData } =
    useGetRechargeReceptionAddressesQuery();

  const binanceData = receptionAddressesResponse?.data.binance;
  const walletAddress = binanceData?.walletAddress?.trim() || '';
  const minAmount = binanceData?.minAmount ?? 1;

  const handleTransactionIdChange = (value: string) => {
    setTransactionId(value);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!transactionId.trim()) {
      setError(t('recharge.binanceClaim.transactionIdRequired'));
      return;
    }

    setError(null);
    await onSubmit(transactionId.trim());
  };

  const handleCopyWalletAddress = async () => {
    if (!walletAddress) return;
    try {
      await navigator.clipboard.writeText(walletAddress);
      toast.success(t('common.copied'));
    } catch {
      toast.error(t('common.copyError'));
    }
  };

  return (
    <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-6" noValidate>
      <div className="bg-neutral-dark/30 rounded-lg p-4">
        <p className="text-sm text-neutral-medium font-body">
          {t('recharge.binanceClaim.description')}
        </p>
      </div>

      <div className="bg-neutral-dark/30 rounded-lg p-4 space-y-3">
        <h3 className="text-base font-heading font-semibold text-brand-accent">
          {t('admin.paymentGateways.binance.title')}
        </h3>

        <div className="space-y-2">
          <span className="text-neutral-medium font-body text-sm block">
            {t('admin.paymentGateways.binance.walletAddress')}:
          </span>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 p-2 bg-neutral-dark/50 rounded-lg">
            <span className="text-neutral-light font-mono text-sm break-all">
              {isFetchingReceptionData
                ? t('common.loading')
                : walletAddress || t('recharge.shamCashClaim.notAvailable')}
            </span>
            {!isFetchingReceptionData && walletAddress ? (
              <button
                type="button"
                onClick={() => void handleCopyWalletAddress()}
                className="p-1.5 bg-brand-accent/20 text-brand-accent rounded hover:bg-brand-accent/30 transition-colors flex-shrink-0 self-end sm:self-auto"
                title={t('common.copy')}
                aria-label={t('common.copy')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              </button>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:justify-between gap-2">
          <span className="text-neutral-medium font-body text-sm">{t('recharge.currency')}:</span>
          <span className="text-neutral-light font-body text-sm">USD</span>
        </div>

        <p className="text-xs text-neutral-medium font-body">
          {minAmount > 0 ? `${t('recharge.minAmount')}: ${minAmount} USD` : t('recharge.noMinAmount')}
        </p>
      </div>

      <div>
        <label
          htmlFor="binanceTransactionId"
          className="block text-sm font-medium text-neutral-light mb-2 font-body"
        >
          {t('recharge.binanceClaim.transactionId')} <span className="text-danger">*</span>
        </label>
        <input
          id="binanceTransactionId"
          name="transactionId"
          type="text"
          value={transactionId}
          onChange={(e) => handleTransactionIdChange(e.target.value)}
          disabled={isLoading}
          placeholder={t('recharge.binanceClaim.transactionIdPlaceholder')}
          className={`w-full px-4 py-3 card-glass rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-300 font-body ${
            error ? 'border-danger' : 'border-neutral-dark'
          }`}
        />
        {error && (
          <p className="mt-1 text-sm text-danger font-body animate-slide-down">{error}</p>
        )}
        <p className="mt-1 text-xs text-neutral-medium font-body">
          {t('recharge.binanceClaim.transactionIdHint')}
        </p>
      </div>

      <button
        type="submit"
        disabled={isLoading || !transactionId.trim()}
        className="w-full px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <>
            <ButtonSpinner />
            <span>{t('common.loading')}</span>
          </>
        ) : (
          t('recharge.binanceClaim.submit')
        )}
      </button>
    </form>
  );
}